/*
 * Valenor — Add-to-Cart handler.
 * Mirrors Horizon's product-form.js submit flow:
 *   1. POST /cart/add.js with `sections` listing every cart-items-component
 *      section id so the response carries their pre-rendered HTML.
 *   2. Dispatch a `cart:update` event whose detail matches what
 *      component-cart-items expects (`detail.data.sections`). The drawer
 *      then morphs its DOM without a second roundtrip.
 *   3. Open <cart-drawer-component> directly (works even when the shop
 *      setting "auto-open cart drawer" is off).
 *
 * Falls back to native submit on failure so the shopper never loses an
 * add-to-cart to a transient JS error.
 */
(() => {
  if (typeof window === 'undefined' || window.__vlnAtcInit) return;
  window.__vlnAtcInit = true;

  const collectSectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      const id = el.dataset && el.dataset.sectionId;
      if (id) ids.add(id);
    });
    return Array.from(ids);
  };

  const openCartDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
        return true;
      } catch (e) {
        console.warn('[vln-atc] drawer.open() failed', e);
      }
    }
    return false;
  };

  const dispatchCartUpdate = (data) => {
    // Mirror the shape Horizon's CartAddEvent produces so cart-icon (and any
    // other listener using `detail.data.itemCount` / `detail.data.source`)
    // updates the bubble correctly. Without `source: 'product-form-component'`
    // and a numeric itemCount, cart-icon resets the bubble to 0 — the cart
    // looks empty even though the line was added server-side.
    const evt = new CustomEvent('cart:update', {
      bubbles: true,
      detail: { data },
    });
    document.dispatchEvent(evt);
  };

  const handleSubmit = async (event) => {
    const form = event.target.closest('.vln-atc form');
    if (!form) return;
    event.preventDefault();
    event.stopPropagation();

    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;

    try {
      const formData = new FormData(form);
      const sectionIds = collectSectionIds();
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }
      const qty = Number(formData.get('quantity')) || 1;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload.status || payload.errors) {
        const msg =
          payload.description ||
          payload.message ||
          `Add to cart failed (${res.status})`;
        throw new Error(msg);
      }

      const productId = form.closest('[data-product-id]')?.dataset?.productId;
      // payload.sections: { 'cart-drawer-section-id': '<html>…</html>' }
      dispatchCartUpdate({
        source: 'product-form-component',
        itemCount: qty,
        productId,
        sections: payload.sections,
      });
      openCartDrawer();
    } catch (err) {
      console.error('[vln-atc]', err);
      form.submit();
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  document.addEventListener('submit', handleSubmit, true);

  // Any button marked with data-vln-sticky-atc OR data-vln-atc-trigger
  // (mid-CTAs, final-CTA, sticky bar…) routes through the main form so
  // users always get the AJAX + cart-drawer flow instead of a scroll.
  const triggerMainForm = (e) => {
    const btn = e.target.closest('[data-vln-sticky-atc], [data-vln-atc-trigger]');
    if (!btn) return;
    e.preventDefault();
    const form = document.querySelector('.vln-atc form');
    if (!form) return;
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  };
  document.addEventListener('click', triggerMainForm);
})();

